import { useMemo, useRef, useState } from "react";
import {
  useListMarketingMaterials,
  useCreateMarketingMaterial,
  useDeleteMarketingMaterial,
  getListMarketingMaterialsQueryKey,
  useGetMe,
} from "@workspace/api-client-react";
import type {
  MarketingMaterial,
  MarketingMaterialCategory,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useUpload } from "@workspace/object-storage-web";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import {
  Megaphone,
  Plus,
  Download,
  Trash2,
  FileText,
  Loader2,
  UploadCloud,
} from "lucide-react";

const MANAGE_ROLES = ["admin", "account_manager", "operations"];

const CATEGORY_LABELS: Record<MarketingMaterialCategory, string> = {
  flyer: "Flyer",
  product: "Product",
  brochure: "Brochure",
  other: "Other",
};

const CATEGORY_ORDER: MarketingMaterialCategory[] = [
  "flyer",
  "product",
  "brochure",
  "other",
];

// Accept the same set the server verifies (PDF + common image types).
const ACCEPT = ".pdf,image/png,image/jpeg,image/gif,image/webp,image/heic";

function isImage(contentType: string): boolean {
  return contentType.startsWith("image/");
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function MarketingMaterials() {
  const { data: me } = useGetMe();
  const canManage = me ? MANAGE_ROLES.includes(me.role) : false;

  const [category, setCategory] = useState<MarketingMaterialCategory | "all">(
    "all",
  );
  const [uploadOpen, setUploadOpen] = useState(false);

  const { data, isLoading, error } = useListMarketingMaterials(
    category === "all" ? undefined : { category },
  );
  const items = useMemo(() => data?.items ?? [], [data]);

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0B132B]">
            Marketing Materials
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Flyers, product sheets and brochures to share with customers.
          </p>
        </div>
        {canManage && (
          <Button
            onClick={() => setUploadOpen(true)}
            className="bg-[#E6007E] hover:bg-[#c4006b] text-white rounded-xl"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Add material
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <CategoryChip
          active={category === "all"}
          onClick={() => setCategory("all")}
          label="All"
        />
        {CATEGORY_ORDER.map((c) => (
          <CategoryChip
            key={c}
            active={category === c}
            onClick={() => setCategory(c)}
            label={CATEGORY_LABELS[c]}
          />
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : error ? (
        <p className="text-sm text-red-600 py-10 text-center">
          Could not load marketing materials. Please try again.
        </p>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="rounded-full bg-gray-100 p-4 mb-4">
            <Megaphone className="w-7 h-7 text-gray-400" />
          </div>
          <p className="font-medium text-[#0B132B]">No materials yet</p>
          <p className="text-sm text-gray-500 mt-1">
            {canManage
              ? "Upload your first flyer or product sheet."
              : "Check back soon for new marketing collateral."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((m) => (
            <MaterialCard key={m.id} material={m} canManage={canManage} />
          ))}
        </div>
      )}

      {canManage && (
        <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} />
      )}
    </div>
  );
}

function CategoryChip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? "rounded-full px-3.5 py-1.5 text-sm font-medium bg-[#0B132B] text-white"
          : "rounded-full px-3.5 py-1.5 text-sm font-medium bg-gray-100 text-gray-600 hover:bg-gray-200"
      }
    >
      {label}
    </button>
  );
}

function MaterialCard({
  material,
  canManage,
}: {
  material: MarketingMaterial;
  canManage: boolean;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const deleteMaterial = useDeleteMarketingMaterial();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const fileSrc = `/api/storage${material.fileUrl}`;

  const handleDelete = async () => {
    try {
      await deleteMaterial.mutateAsync({ id: material.id });
      queryClient.invalidateQueries({
        queryKey: getListMarketingMaterialsQueryKey(),
      });
      toast({
        title: "Material removed",
        description: `"${material.title}" has been deleted.`,
      });
      setConfirmOpen(false);
    } catch {
      toast({
        variant: "destructive",
        title: "Could not delete",
        description: "Please try again.",
      });
    }
  };

  return (
    <Card className="overflow-hidden rounded-2xl border-gray-200 flex flex-col">
      <div className="aspect-[4/3] bg-gray-50 flex items-center justify-center overflow-hidden">
        {isImage(material.contentType) ? (
          <img
            src={fileSrc}
            alt={material.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <FileText className="w-12 h-12 text-gray-300" />
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-[#0B132B] leading-snug">
            {material.title}
          </h3>
          <Badge className="shrink-0 bg-gray-100 text-gray-600 hover:bg-gray-100">
            {CATEGORY_LABELS[material.category]}
          </Badge>
        </div>
        {material.description && (
          <p className="text-sm text-gray-500 mt-1.5 line-clamp-2">
            {material.description}
          </p>
        )}
        <p className="text-xs text-gray-400 mt-2">
          {material.fileName} · {formatBytes(material.fileSizeBytes)}
        </p>
        <div className="mt-4 flex items-center gap-2">
          <a
            href={fileSrc}
            target="_blank"
            rel="noopener noreferrer"
            download={material.fileName}
            className="flex-1"
          >
            <Button
              variant="outline"
              className="w-full rounded-xl border-gray-200"
            >
              <Download className="w-4 h-4 mr-1.5" />
              Download
            </Button>
          </a>
          {canManage && (
            <Button
              variant="outline"
              size="icon"
              onClick={() => setConfirmOpen(true)}
              className="rounded-xl border-gray-200 text-gray-500 hover:text-red-600"
              aria-label="Delete material"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this material?</AlertDialogTitle>
            <AlertDialogDescription>
              "{material.title}" will be removed for everyone. This cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleDelete();
              }}
              disabled={deleteMaterial.isPending}
              className="rounded-xl bg-red-600 hover:bg-red-700 text-white"
            >
              {deleteMaterial.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function UploadDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const createMaterial = useCreateMarketingMaterial();
  const { uploadFile, isUploading } = useUpload();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<MarketingMaterialCategory>("flyer");
  const [file, setFile] = useState<File | null>(null);

  const reset = () => {
    setTitle("");
    setDescription("");
    setCategory("flyer");
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const busy = isUploading || createMaterial.isPending;

  const submit = async () => {
    if (!title.trim() || !file) return;
    try {
      const uploaded = await uploadFile(file);
      if (!uploaded) {
        toast({
          variant: "destructive",
          title: "Upload failed",
          description: "The file could not be uploaded. Please try again.",
        });
        return;
      }
      await createMaterial.mutateAsync({
        data: {
          title: title.trim(),
          description: description.trim() || undefined,
          category,
          objectPath: uploaded.objectPath,
          fileName: file.name,
          contentType: file.type || "application/octet-stream",
          fileSizeBytes: file.size,
        },
      });
      queryClient.invalidateQueries({
        queryKey: getListMarketingMaterialsQueryKey(),
      });
      toast({
        title: "Material added",
        description: `"${title.trim()}" is now available to the network.`,
      });
      reset();
      onOpenChange(false);
    } catch {
      toast({
        variant: "destructive",
        title: "Could not save material",
        description: "Please try again.",
      });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!busy) {
          if (!o) reset();
          onOpenChange(o);
        }
      }}
    >
      <DialogContent className="max-w-[480px] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-[#0B132B]">
            Add marketing material
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Openserve Fibre Flyer"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Category</Label>
            <Select
              value={category}
              onValueChange={(v) =>
                setCategory(v as MarketingMaterialCategory)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_ORDER.map((c) => (
                  <SelectItem key={c} value={c}>
                    {CATEGORY_LABELS[c]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Description (optional)</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="A short note about this material."
              rows={3}
            />
          </div>
          <div className="space-y-1.5">
            <Label>File</Label>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full rounded-xl border border-dashed border-gray-300 px-4 py-6 flex flex-col items-center justify-center text-center hover:border-[#E6007E] transition-colors"
            >
              <UploadCloud className="w-6 h-6 text-gray-400 mb-2" />
              <span className="text-sm text-[#0B132B] font-medium">
                {file ? file.name : "Choose a PDF or image"}
              </span>
              <span className="text-xs text-gray-400 mt-0.5">
                PDF, PNG, JPG, GIF, WEBP or HEIC
              </span>
            </button>
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={submit}
            disabled={!title.trim() || !file || busy}
            className="bg-[#E6007E] hover:bg-[#c4006b] text-white rounded-xl w-full"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Add material"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
